import { useState } from 'react';
import { Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import { errorMessage } from '../../../lib/errorMessage';
import { formatDuration, summarizeHours } from '../../hours/hoursMath';
import {
  useActiveTimer,
  useDeleteTimeEntry,
  useProjectTimeEntries,
  useStartTimer,
  useStopTimer,
  type TimeEntry,
} from '../../hours/hoursQueries';
import { TimeEntryDrawer } from '../../hours/TimeEntryDrawer';
import type { ProjectRow } from './projects.schema';
import styles from './projects.module.css';

function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(amount)}`;
}

interface Props {
  project: ProjectRow;
  projectOptions: { id: string; name: string }[];
  orgId: string;
  userId: string | null;
}

export function ProjectHoursTab({ project, projectOptions, orgId, userId }: Props) {
  const toast = useToast();
  const { data: entries = [], isLoading, error } = useProjectTimeEntries(orgId, project.id);
  const { data: activeTimer } = useActiveTimer(orgId, userId);
  const startTimer = useStartTimer(orgId, userId);
  const stopTimer = useStopTimer(orgId, userId);
  const deleteEntry = useDeleteTimeEntry(orgId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);

  const summary = summarizeHours(entries, project.hourly_rate);
  const timerHere = activeTimer?.project_id === project.id;

  async function start() {
    try {
      await startTimer.mutateAsync({ project_id: project.id, description: null, billable: true });
      toast.success('הטיימר הופעל');
    } catch (err) {
      toast.error(`לא הצלחנו להפעיל · ${errorMessage(err)}`);
    }
  }

  async function stop() {
    if (!activeTimer) return;
    try {
      await stopTimer.mutateAsync(activeTimer.id);
      toast.success('הטיימר נעצר');
    } catch (err) {
      toast.error(`לא הצלחנו לעצור · ${errorMessage(err)}`);
    }
  }

  async function remove(entry: TimeEntry) {
    if (!window.confirm('למחוק את רישום השעות?')) return;
    try {
      await deleteEntry.mutateAsync(entry.id);
      toast.success('הרישום נמחק');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  const overValue = project.value_amount > 0 && summary.billableValue > project.value_amount;

  return (
    <div className={styles.overview}>
      <div className={styles.overviewGrid}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>סה"כ שעות</span>
          <span className={styles.kpiValue}>
            <bdi>{formatDuration(summary.totalMinutes)}</bdi>
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>שעות לחיוב</span>
          <span className={styles.kpiValue}>
            <bdi>{formatDuration(summary.billableMinutes)}</bdi>
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>שווי השעות</span>
          <span className={cn(styles.kpiValue, overValue && styles.kpiWarn)}>
            <bdi>{formatILS(summary.billableValue)}</bdi>
          </span>
          <span className={styles.kpiSub}>
            {project.hourly_rate === null
              ? 'אין תעריף לשעה בפרויקט'
              : `לפי ${formatILS(project.hourly_rate)} לשעה`}
            {project.value_amount > 0 ? ` · ערך הפרויקט ${formatILS(project.value_amount)}` : ''}
          </span>
        </div>
      </div>

      {summary.unpricedMinutes > 0 ? (
        <p className={styles.notice}>
          <bdi>{formatDuration(summary.unpricedMinutes)}</bdi> לחיוב בלי תעריף, ולכן לא נספרו בשווי.
          הוסף תעריף לשעה בעריכת הפרויקט.
        </p>
      ) : null}

      <div className={styles.sectionCard}>
        <div className={styles.sectionCardHead}>
          <h3>רישומי שעות</h3>
          <div className={styles.workspaceActions}>
            {timerHere ? (
              <button
                type="button"
                className={styles.btnDanger}
                disabled={stopTimer.isPending}
                onClick={() => {
                  void stop();
                }}
              >
                <Square size={14} aria-hidden="true" />
                עצור טיימר
              </button>
            ) : activeTimer ? (
              <span className={styles.kpiSub}>טיימר פעיל בפרויקט אחר</span>
            ) : (
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={startTimer.isPending}
                onClick={() => {
                  void start();
                }}
              >
                <Play size={14} aria-hidden="true" />
                התחל טיימר
              </button>
            )}
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              <Plus size={14} aria-hidden="true" />
              הוסף שעות
            </button>
          </div>
        </div>

        {isLoading ? <p className={styles.notice}>טוען...</p> : null}
        {error ? (
          <p className={styles.notice}>לא הצלחנו לטעון שעות · {errorMessage(error)}</p>
        ) : null}
        {!isLoading && !error && entries.length === 0 ? (
          <p className={styles.notice}>אין שעות על הפרויקט. הוסף שעות או התחל טיימר.</p>
        ) : null}

        {entries.length > 0 ? (
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>על מה</th>
                  <th>משך</th>
                  <th>חיוב</th>
                  <th aria-label="פעולות" />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <bdi>{format(new Date(e.started_at), 'dd/MM/yy HH:mm', { locale: he })}</bdi>
                    </td>
                    <td className={styles.wrap}>{e.description ?? '—'}</td>
                    <td>
                      {e.duration_minutes === null ? (
                        'רץ עכשיו'
                      ) : (
                        <bdi>{formatDuration(e.duration_minutes)}</bdi>
                      )}
                    </td>
                    <td>{e.billable ? 'לחיוב' : 'לא לחיוב'}</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          aria-label="ערוך רישום"
                          onClick={() => {
                            setEditing(e);
                            setDrawerOpen(true);
                          }}
                        >
                          <Pencil size={13} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          aria-label="מחק רישום"
                          disabled={deleteEntry.isPending}
                          onClick={() => {
                            void remove(e);
                          }}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <TimeEntryDrawer
        open={drawerOpen}
        entry={editing}
        defaultProjectId={project.id}
        projects={projectOptions}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
